import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { DatasheetLinks } from "@/components/DatasheetLinks";

import { CallbackRail } from "@/components/CallbackRail";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES, BBN_SECURITY } from "@/lib/images";

const PDF = "/datablad";

const FAQS: FaqItem[] = [
  {
    q: "Vad gör 3M Ultra annorlunda?",
    a: "Ultra består av upp till 42 mikrotunna polymerlager som lamineras ihop. Det gör filmen tunnare än traditionell säkerhetsfilm, med upp till 5× högre rivhållfasthet. Filmen 'studsar tillbaka' vid slag i stället för att rivas isär.",
  },
  {
    q: "Vilka standarder klarar Ultra?",
    a: "3M Ultra S600/S800 uppfyller EN 12600 (pendelslagstest för säkerhetsglas) upp till klass 2B2, EN 356 upp till P2A (S800), ASTM E84 brandklass A samt ANSI Z97.1.",
  },
  {
    q: "Behövs kantförstärkning?",
    a: "För maximal inbrottsfördröjning kombineras Ultra med 3M Impact Protection Attachment, en säkerhetsfog eller mekanisk infästning som binder filmen till karmen så hela glaspartiet hålls kvar i ramen.",
  },
  {
    q: "Hur lång garanti?",
    a: "Upp till 15 års produktgaranti från 3M och 10 års arbetsgaranti från BBN Nordic vid invändig montering.",
  },
];

export const Route = createFileRoute("/3m-ultra-sakerhetsfilm")({
  head: () => ({
    meta: [
      { title: "3M Ultra säkerhetsfilm — S600 & S800 | BBN Nordic" },
      { name: "description", content: "3M Ultra S600 & S800 — patenterad flerlagerteknik för maximalt inbrottsskydd och splitterskydd. EN 12600, EN 356 P2A. Auktoriserad installation." },
      { property: "og:title", content: "3M Ultra säkerhetsfilm" },
      { property: "og:description", content: "3M:s flaggskepp — upp till 42 polymerlager, EN 12600 & EN 356 P2A." },
      { property: "og:type", content: "product" },
      { property: "og:url", content: "https://bbnnordic.se/3m-ultra-sakerhetsfilm" },
      { property: "og:image", content: IMAGES.sakerhetsfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-ultra-sakerhetsfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M Ultra säkerhetsfilm",
          category: "Fönsterfilm / Säkerhetsfilm",
          description:
            "3M Ultra S600 & S800 — patenterad flerlagerteknik (upp till 42 lager) för maximalt inbrottsskydd och splitterskydd. Uppfyller EN 12600 klass 2B2 och EN 356 P2A.",
          image: IMAGES.sakerhetsfilm,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          material: "Flerlagerlaminat av polyester, upp till 42 lager",
          url: "https://bbnnordic.se/3m-ultra-sakerhetsfilm",
          additionalProperty: [
            { "@type": "PropertyValue", name: "Tjocklek", value: "200 μm (8 mil)", unitText: "μm" },
            { "@type": "PropertyValue", name: "Antal polymerlager", value: "Upp till 42" },
            { "@type": "PropertyValue", name: "Ljustransmission (VLT)", value: ">88 %", unitText: "%" },
            { "@type": "PropertyValue", name: "Standard", value: "EN 12600 klass 2B2, EN 356 P2A, ANSI Z97.1, ASTM E84" },
            { "@type": "PropertyValue", name: "Garanti", value: "Upp till 15 år produktgaranti + 10 år arbetsgaranti" },
          ],
          hasVariant: [
            { "@type": "Product", name: "3M Ultra S600", description: "6 mil (150 μm) — EN 12600 2B2" },
            { "@type": "Product", name: "3M Ultra S800", description: "8 mil (200 μm) — EN 12600 2B2 + EN 356 P2A" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "950",
            priceSpecification: { "@type": "PriceSpecification", price: "950", priceCurrency: "SEK" },
            price: "950",
            highPrice: "2200",
            offerCount: "2",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2026-12-31",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Säkerhetsfilm", item: "https://bbnnordic.se/sakerhetsfilm" },
            { "@type": "ListItem", position: 3, name: "3M Ultra", item: "https://bbnnordic.se/3m-ultra-sakerhetsfilm" },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <ContentPage
      eyebrow="3M ULTRA"
      title="3M Ultra säkerhetsfilm — S600 & S800"
      subtitle="3M Ultra är en säkerhetsfilm med patenterad flerlagerteknik för högsta klassen av inbrotts- och splitterskydd."
      heroImage={BBN_SECURITY[2]}
      breadcrumbs={[{ label: "Säkerhetsfilm", to: "/sakerhetsfilm" }, { label: "3M Ultra" }]}
      sections={[
        {
          eyebrow: "TEKNIK",
          heading: "Upp till 42 polymerlager ger hög rivhållfasthet.",
          paragraphs: [
            "I 3M Ultra lamineras upp till 42 mikrotunna polymerlager ihop till en patenterad kompositstruktur. Filmen är tunnare än traditionell säkerhetsfilm men har upp till 5 gånger högre rivhållfasthet. Vid slag studsar den tillbaka i stället för att rivas isär.",
            "Både S600 och S800 är optiskt klara och uppfyller EN 12600. Ultra S800 är dessutom klassad enligt EN 356 P2A, det starkaste av 3M:s standardsortiment för inbrottsskydd.",
          ],
          bullets: [
            "42-lagers patenterad polymerkomposit",
            "Upp till 5× högre rivhållfasthet än konventionell film",
            "EN 12600 (pendelslag) upp till klass 2B2",
            "EN 356 P2A på Ultra S800 (inbrottsskydd)",
            "ASTM E84 brandklass A",
            "Optiskt klar — >88 % ljustransmission",
          ],
          image: BBN_SECURITY[5],
        },
        {
          eyebrow: "ANVÄNDNING",
          heading: "Miljöer där vi använder Ultra.",
          bullets: [
            "Juvelerare, optik, apotek, bank och vapenförvaring",
            "Ambassader, myndigheter och skyddsobjekt",
            "Butiker med exponerade varor mot allmän plats",
            "Kontor i markplan med förhöjd hotbild",
            "Explosionsskydd — vid större projekt gör vi tryck- och riskanalys per fasad",
          ],
          image: IMAGES.installer,
        },
        {
          eyebrow: "GARANTI",
          heading: "Upp till 15 år från 3M.",
          paragraphs: [
            "3M lämnar upp till 15 års produktgaranti på Ultra vid invändig montering. Vi är auktoriserad installatör och lämnar dessutom 10 års arbetsgaranti.",
          ],
          extra: (
            <a
              href={PDF}
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-semibold hover:bg-navy/90 transition-colors"
            >
              Se alla datablad →
            </a>
          ),
        },
      ]}
      related={[
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt", desc: "Se de olika säkerhetsfilmerna." },
        { to: "/3m-sakerhetsfilm-med-solfilm", label: "Kombifilm", desc: "Säkerhet + solfilm i samma film." },
        { to: "/3m-sakerhetsfilm-utan-solfilm", label: "Klar säkerhetsfilm", desc: "När glaset ska förbli helt orört." },
      ]}
    >
      <DatasheetLinks
        items={[
          { name: "3M Ultra S600 och S800", href: "/datablad/3m-ultra-series-family-card.pdf" },
          { name: "3M Ultra S600", href: "/datablad/3m-ultra-s600.pdf" },
        ]}
      />
      <FaqSection heading="Vanliga frågor om 3M Ultra." items={FAQS} />
      
      <CallbackRail product="Säkerhetsfilm" />
    </ContentPage>
  ),
});
